import { ReactNode } from 'react';
import clsx from 'clsx';
import { LayoutGrid, List } from 'lucide-react';

// Ports of ListingSummary / ListingViewToggle from
// slightercrm/src/components/whatsapp/WhatsAppUi.tsx, so admin list pages share
// the CRM's KPI strip and grid/list switch.

export function ListingSummary({ items }: {
  items: { label: string; value: ReactNode; detail: ReactNode; icon: ReactNode }[];
}) {
  return (
    <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-border bg-surface lg:flex">
      {items.map((item) => (
        <div key={item.label} className="min-w-0 flex-1 border-b border-r border-border p-5 last:border-r-0 lg:border-b-0">
          <div className="mb-3 flex items-center justify-between gap-2 text-sm text-text-secondary">
            {item.label}
            <span className="text-primary">{item.icon}</span>
          </div>
          <div className="text-3xl font-semibold tracking-tight text-text-primary tabular-nums">{item.value}</div>
          <div className="mt-1.5 text-xs text-text-muted">{item.detail}</div>
        </div>
      ))}
    </div>
  );
}

export function ListingViewToggle({ value, onChange }: {
  value: 'grid' | 'list';
  onChange: (value: 'grid' | 'list') => void;
}) {
  return (
    <div className="inline-flex shrink-0 gap-1 rounded-xl border border-border bg-surface-alt p-1" role="group" aria-label="Display layout">
      {([
        { key: 'grid', label: 'Grid view', Icon: LayoutGrid },
        { key: 'list', label: 'List view', Icon: List },
      ] as const).map(({ key, label, Icon }) => (
        <button
          key={key}
          type="button"
          aria-label={label}
          title={label}
          aria-pressed={value === key}
          onClick={() => onChange(key)}
          className={clsx(
            'flex h-8 w-9 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            value === key ? 'bg-surface text-primary shadow-sm' : 'text-text-muted hover:text-text-primary',
          )}
        >
          <Icon size={17} />
        </button>
      ))}
    </div>
  );
}

/** Underlined filter tabs — the CRM's status-filter row. */
export function UnderlineTabs<T extends string>({ tabs, value, onChange, label }: {
  tabs: { value: T; label: string; count?: number; dot?: string }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-5 overflow-x-auto" role="group" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          aria-pressed={value === t.value}
          className={clsx(
            'inline-flex shrink-0 items-center gap-1.5 border-b-2 pb-3 text-sm font-medium transition-colors',
            value === t.value ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-primary',
          )}
        >
          {t.dot && <span className={clsx('h-1.5 w-1.5 rounded-full', t.dot)} />}
          {t.label}
          {typeof t.count === 'number' && <span className="text-xs tabular-nums opacity-70">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
